import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Cross, Phone, MapPin, Navigation, Loader2, ExternalLink, FileText, Clock, Shield } from 'lucide-react';
import { api } from '@/lib/api';
import { useLocation } from '@/contexts/LocationContext';

const PharmacyDutyView = ({ language, onBack, pdfZones }) => {
  const [pharmacies, setPharmacies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userLat, setUserLat] = useState(null);
  const [userLng, setUserLng] = useState(null);
  const [locating, setLocating] = useState(false);
  const [showPDFs, setShowPDFs] = useState(false);
  const [dutyDateDisplay, setDutyDateDisplay] = useState('');
  const [nextMorning, setNextMorning] = useState('');
  const requestSeq = useRef(0);

  const t = (es, en, de) => language === 'es' ? es : language === 'de' ? de : en;

  const { location: ctxLocation, isRealLocation } = useLocation();

  useEffect(() => {
    if (isRealLocation && ctxLocation?.lat) {
      setUserLat(ctxLocation.lat);
      setUserLng(ctxLocation.lng);
      setLocating(false);
    }
  }, [isRealLocation, ctxLocation?.lat, ctxLocation?.lng]);

  useEffect(() => {
    if (isRealLocation) return;
    if (navigator.geolocation) {
      setLocating(true);
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setUserLat(pos.coords.latitude);
          setUserLng(pos.coords.longitude);
          setLocating(false);
        },
        () => setLocating(false),
        { timeout: 5000 }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadPharmacies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userLat, userLng]);

  const loadPharmacies = async () => {
    const reqId = ++requestSeq.current;
    setLoading(true);
    try {
      const params = {};
      if (userLat && userLng) {
        params.lat = userLat;
        params.lng = userLng;
      }
      const res = await api.getDutyPharmacies(params);
      if (reqId !== requestSeq.current) return; // stale response (a newer request with location is in flight)
      const data = res.data;
      // Handle new response format
      if (data.pharmacies) {
        setPharmacies(data.pharmacies);
        setDutyDateDisplay(data.duty_date_display || '');
        setNextMorning(data.next_morning || '');
      } else {
        // Backwards compat with old array format
        setPharmacies(Array.isArray(data) ? data : []);
      }
    } catch (e) {
      console.error('Failed to load pharmacies:', e);
    } finally { if (reqId === requestSeq.current) setLoading(false); }
  };

  const mapsUrl = (p) => p.google_place_id
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent('Farmacia ' + p.pharmacist)}&query_place_id=${p.google_place_id}`
    : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent('Farmacia ' + p.pharmacist + ', ' + p.address + ', ' + p.zone + ', Tenerife')}`;

  const nearest = userLat && pharmacies.length > 0 && pharmacies[0].distance_km < 999 ? pharmacies[0] : null;

  const getScheduleLabel = (pharmacy) => {
    if (pharmacy.schedule === '24h') {
      return { text: '24h', color: 'bg-emerald-500 text-white' };
    }
    if (pharmacy.schedule) {
      return { text: pharmacy.schedule, color: 'bg-amber-100 text-amber-800' };
    }
    return { text: t('Toda la noche', 'All night', 'Ganze Nacht'), color: 'bg-blue-100 text-blue-800' };
  };

  if (showPDFs) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] pb-20" data-testid="pharmacy-pdf-page">
        <div className="bg-emerald-600 px-5 pt-6 pb-6 safe-top">
          <button onClick={() => setShowPDFs(false)} className="bg-white/20 rounded-full p-2 mb-3" data-testid="pdf-back-btn">
            <ArrowLeft size={20} className="text-white" />
          </button>
          <h1 className="text-xl font-bold text-white">{t('Turnos completos (PDF)', 'Full schedules (PDF)', 'Vollstandige Plane (PDF)')}</h1>
        </div>
        <div className="px-4 mt-4 space-y-4">
          {pdfZones.map((zone, zi) => (
            <div key={zi} className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
                <h2 className="font-bold text-sm text-[#1D1D1F] flex items-center gap-2">
                  <MapPin size={14} className="text-emerald-600" />
                  {zone[`zone_${language}`] || zone.zone_es}
                </h2>
              </div>
              <div className="divide-y divide-gray-50">
                {zone.areas.map((area, ai) => (
                  <a key={ai} href={area.url} target="_blank" rel="noopener noreferrer"
                    className="flex items-center justify-between px-4 py-3 hover:bg-emerald-50 transition-colors">
                    <div className="flex items-center gap-3">
                      <FileText size={16} className="text-emerald-500" />
                      <span className="text-sm font-medium text-gray-700">{area.name}</span>
                    </div>
                    <span className="text-xs text-emerald-600 font-medium">PDF &rarr;</span>
                  </a>
                ))}
              </div>
            </div>
          ))}
          <div className="text-center py-4">
            <a href="https://www.coftenerife.es/ciudadanos/farmacias-de-guardia/" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-xs text-gray-400 hover:text-emerald-600">
              <ExternalLink size={12} />
              {t('Fuente: COF S/C de Tenerife', 'Source: COF Tenerife', 'Quelle: COF Teneriffa')}
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] pb-20" data-testid="pharmacy-page">
      {/* Header */}
      <div className="relative bg-gradient-to-br from-emerald-600 to-emerald-700 px-5 pt-6 pb-8">
        <button onClick={onBack} className="absolute top-4 left-4 bg-white/20 backdrop-blur-sm rounded-full p-2" data-testid="pharmacy-back-btn">
          <ArrowLeft size={20} className="text-white" />
        </button>
        <div className="text-center pt-8">
          <div className="w-16 h-16 mx-auto mb-3 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
            <Cross size={32} className="text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white">{t('Farmacias de Guardia', 'Duty Pharmacies', 'Notdienstapotheken')}</h1>
          {dutyDateDisplay && nextMorning && (
            <p className="text-white/90 text-sm mt-2 font-medium" data-testid="duty-night-label">
              {t(
                `Noche del ${dutyDateDisplay} al ${nextMorning}`,
                `Night of ${dutyDateDisplay} to ${nextMorning}`,
                `Nacht vom ${dutyDateDisplay} bis ${nextMorning}`
              )}
            </p>
          )}
          <div className="flex items-center justify-center gap-3 mt-3">
            <a href="tel:112" className="inline-flex items-center gap-2 bg-red-500/80 text-white font-semibold text-sm px-4 py-2 rounded-full" data-testid="emergency-call-btn">
              <Phone size={14} /> 112
            </a>
            <button onClick={() => setShowPDFs(true)} className="inline-flex items-center gap-2 bg-white/20 text-white text-sm px-4 py-2 rounded-full" data-testid="view-pdfs-btn">
              <FileText size={14} /> {t('Ver turnos PDF', 'View PDF schedules', 'PDF-Plane')}
            </button>
          </div>
        </div>
      </div>

      {/* Location status */}
      {locating && (
        <div className="mx-4 -mt-3 bg-amber-50 border border-amber-100 rounded-xl px-4 py-2 flex items-center gap-2 mb-3">
          <Loader2 size={14} className="animate-spin text-amber-500" />
          <span className="text-xs text-amber-700">{t('Obteniendo tu ubicacion...', 'Getting your location...', 'Standort wird ermittelt...')}</span>
        </div>
      )}
      {userLat && !locating && (
        <div className="mx-4 -mt-3 bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-2 flex items-center gap-2 mb-3">
          <Navigation size={14} className="text-emerald-500" />
          <span className="text-xs text-emerald-700">{t('Ordenadas por cercania', 'Sorted by proximity', 'Nach Nahe sortiert')}</span>
        </div>
      )}

      {/* Nearest pharmacy highlight */}
      {nearest && !loading && (
        <div className="mx-4 mt-2 mb-4 rounded-2xl overflow-hidden shadow-md border border-emerald-200 bg-white" data-testid="nearest-pharmacy-card">
          <div className="bg-emerald-600 text-white px-4 py-2 flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5">
              <Navigation size={12} /> {t('Farmacia más cercana', 'Nearest pharmacy', 'Nächste Apotheke')}
            </span>
            <span className="text-sm font-bold" data-testid="nearest-pharmacy-distance">
              {nearest.distance_km < 1 ? `${Math.round(nearest.distance_km * 1000)} m` : `${nearest.distance_km} km`}
            </span>
          </div>
          <div className="px-4 py-3">
            <h3 className="font-semibold text-[#1D1D1F] text-base leading-tight" data-testid="nearest-pharmacy-name">
              {nearest.google_name || nearest.pharmacist}
            </h3>
            <p className="text-sm text-gray-500 mt-0.5">{nearest.address} · {nearest.zone}</p>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${getScheduleLabel(nearest).color}`}>
                <Clock size={10} /> {getScheduleLabel(nearest).text}
              </span>
              {nearest.is_permanent && (
                <span className="bg-emerald-500 text-white text-xs font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1"><Shield size={10} /> {t('Fija', 'Permanent', 'Permanent')}</span>
              )}
            </div>
            <div className="flex gap-2 mt-3">
              <a href={mapsUrl(nearest)} target="_blank" rel="noopener noreferrer"
                className="flex-1 inline-flex items-center justify-center gap-2 bg-[#38B2AC] hover:bg-[#005A63] text-white font-semibold text-sm py-3 rounded-full transition-colors"
                data-testid="nearest-pharmacy-maps-btn">
                <MapPin size={16} /> Google Maps
              </a>
              {nearest.phone && (
                <a href={`tel:${nearest.phone.replace(/\s/g, '')}`}
                  className="w-12 h-12 flex-shrink-0 inline-flex items-center justify-center bg-emerald-500 hover:bg-emerald-600 text-white rounded-full transition-colors"
                  data-testid="nearest-pharmacy-call-btn">
                  <Phone size={16} />
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Pharmacy list */}
      <div className="px-4 mt-2 space-y-3">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 size={24} className="animate-spin text-emerald-500" />
          </div>
        ) : pharmacies.length === 0 ? (
          <div className="text-center py-12" data-testid="no-pharmacies">
            <Cross size={40} className="mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500">{t('No hay datos de farmacias para hoy', 'No pharmacy data for today', 'Keine Apothekendaten fur heute')}</p>
            <button onClick={() => setShowPDFs(true)} className="mt-4 text-emerald-600 text-sm font-semibold">{t('Consultar turnos en PDF', 'Check PDF schedules', 'PDF-Plane prufen')}</button>
          </div>
        ) : (
          pharmacies.map((p, i) => {
            const schedule = getScheduleLabel(p);
            return (
              <div key={p.id || i} className={`bg-white rounded-2xl shadow-sm overflow-hidden border ${p.is_permanent ? 'border-emerald-200' : 'border-gray-100'}`} data-testid={`pharmacy-item-${i}`}>
                <div className="px-4 py-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        {p.distance_km !== undefined && p.distance_km < 999 && (
                          <span className="bg-emerald-100 text-emerald-700 text-xs font-bold px-2 py-0.5 rounded-full" data-testid={`pharmacy-distance-${i}`}>
                            {p.distance_km} km
                          </span>
                        )}
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${schedule.color}`}>
                          <Clock size={10} />
                          {schedule.text}
                        </span>
                        {p.is_permanent && (
                          <span className="bg-emerald-500 text-white text-xs font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                            <Shield size={10} />
                            {t('Fija', 'Permanent', 'Permanent')}
                          </span>
                        )}
                      </div>
                      <h3 className="font-semibold text-[#1D1D1F] text-sm">{p.pharmacist}</h3>
                      <a
                        href={mapsUrl(p)}
                        target="_blank" rel="noopener noreferrer"
                        className="text-xs text-gray-500 hover:text-blue-500 transition-colors mt-0.5 block"
                        data-testid={`pharmacy-address-${i}`}
                      >
                        <MapPin size={12} className="inline mr-1" />
                        {p.address}
                      </a>
                      <span className="text-xs text-gray-400 mt-0.5 block">{p.zone}</span>
                    </div>
                    <a href={`tel:${p.phone?.replace(/\s/g, '')}`}
                      className="flex-shrink-0 ml-3 bg-emerald-500 text-white rounded-full w-10 h-10 flex items-center justify-center hover:bg-emerald-600 transition-colors"
                      data-testid={`pharmacy-call-${i}`}>
                      <Phone size={16} />
                    </a>
                  </div>
                  {p.phone && <p className="text-xs text-gray-400 mt-1">{p.phone}</p>}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Footer */}
      <div className="text-center py-6 px-4">
        <p className="text-[11px] text-gray-400 leading-relaxed">
          {t(
            'Datos obtenidos del Colegio Oficial de Farmaceuticos de S/C de Tenerife. Farmacias permanentes verificadas en 2026. Recomendamos confirmar llamando antes de desplazarse.',
            'Data from the Official College of Pharmacists of Tenerife. Permanent pharmacies verified in 2026. We recommend calling to confirm before visiting.',
            'Daten vom Offiziellen Apothekerkollegium von Teneriffa. Dauerabotheken 2026 verifiziert. Wir empfehlen, vor dem Besuch anzurufen.'
          )}
        </p>
      </div>
    </div>
  );
};

export default PharmacyDutyView;
